import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../app/store';
import { updateQuantity, removeFromCart, clearCart } from './cartSlice';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Grid,
  Divider,
  Link
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';

export const Cart: React.FC = () => {
  const cartItems = useSelector((state: RootState) => state.cart.items);
  const dispatch = useDispatch();

  const handleUpdateQuantity = (productId: string, quantity: number) => {
    const item = cartItems.find(item => item.id === productId);
    if (item) {
      const newQuantity = Math.max(1, item.quantity + quantity);
      dispatch(updateQuantity({ id: productId, quantity: newQuantity }));
    }
  };

  const handleRemoveFromCart = (productId: string) => {
    dispatch(removeFromCart(productId));
  };

  const handleClearCart = () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      dispatch(clearCart());
    }
  };

  const handleCheckout = () => {
    alert('Proceeding to checkout. This feature is not yet implemented.');
  };

  const total = cartItems.reduce((sum: number, item: any) => sum + item.price * item.quantity, 0);

  return (
    <Box sx={{ maxWidth: 800, margin: 'auto', mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Shopping Cart
      </Typography>
      {cartItems.length > 0 ? (
        <>
          {cartItems.map((item: any) => (
            <Card key={item.id} sx={{ mb: 2 }}>
              <CardContent>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="h6">{item.name}</Typography>
                    <Typography variant="body1">Price: USD {parseFloat(String(item.price)).toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Box display="flex" alignItems="center">
                      <IconButton 
                        onClick={() => handleUpdateQuantity(item.id, -1)} 
                        size="small" 
                        disabled={item.quantity === 1}
                      >
                        <RemoveIcon />
                      </IconButton>
                      <Typography sx={{ mx: 1 }}>{item.quantity}</Typography>
                      <IconButton onClick={() => handleUpdateQuantity(item.id, 1)} size="small">
                        <AddIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton onClick={() => handleRemoveFromCart(item.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" align="right" gutterBottom>
            Total: USD {total.toFixed(2)}
          </Typography>
          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button variant="contained" color="error" onClick={handleClearCart}>
              Clear Cart
            </Button>
            <Button variant="contained" color="primary" onClick={handleCheckout}>
              Proceed to Checkout
            </Button>
          </Box>
        </>
      ) : (
        <Typography variant="body1">Your cart is empty.</Typography>
      )}
      <Box mt={2}>
        <Link href="/" underline="none">
          <Button variant="outlined">Continue Shopping</Button>
        </Link>
      </Box>
    </Box>
  );
};
